// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { NumberTextFieldValue } from "@store/Framework/Common/Fields/Toolkit/numberText";
import CarouselArrow, {
    CarouselArrowPresentationProps,
    CarouselArrowProps,
} from "@store/Library/Components/Toolkit/Carousel/CarouselArrow";
import React, { createRef, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { css, ThemeProps, withTheme } from "styled-components";

/* Starter Custom Code - Start */
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { TextAlignment, textAlignmentOptions } from "@store/Framework/Common/Fields/Toolkit/textAlignment";
import { carouselProps } from "@store/Themes/common/props/carousel";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
/* Starter Custom Code - End */

// Store Custom Code
import { BreakpointValue } from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { DataOption, DataOptions } from "@toolkit/Framework/Common/Utilities/dataOptions";
import { DataType } from "csstype";
import useEmblaCarousel from "embla-carousel-react";
import debounce from "lodash/debounce";

export interface CustomCarouselClassNames {
    carouselWrapper?: string;
    carouselContainer?: string;
    carouselSlidesContainer?: string;
    carouselSlide?: string;
    carouselSlideInner?: string;
    prevArrowWrapper?: string;
    nextArrowWrapper?: string;
    dotsWrapper?: string;
    dotButton?: string;
    dotButtonSelected?: string;
    carouselTitle?: string;
    carouselDot?: string;
}
interface OwnProps {
    id: string;
    extendedStyles?: CarouselStyles;
    title?: string; // Starter Custom Code
    maxNumberOfColumns: number;
    slides: CarouselSlide[];
    // Starter Custom Code
    showDots?: boolean;
    titleAlignment?: TextAlignment;
    useSkipNav?: boolean;
    // Store Custom Code - >
    slideInterval?: number;
    startIndex?: number;
    slideChangeCallback?: (index: number) => void;
    carouselClassNames?: CustomCarouselClassNames;
    // Store Custom Code - <---
}

type Props = OwnProps & ThemeProps<BaseTheme> & HasShellContext;

export interface CarouselSlide {
    id: string;
    renderComponent: (options: CarouselSlideOptions) => ReactNode;
}

export interface CarouselSlideOptions {}

export interface CarouselStyles {
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    // Starter Custom Code
    prevArrowWrapper?: InjectableCss;
    nextArrowWrapper?: InjectableCss;
    dotsWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    dotButtonSelected?: ButtonPresentationProps;
}

export const carouselStyles: CarouselStyles = {
    titleText: carouselProps.titleText,
    prevArrowWrapper: carouselProps.prevArrowWrapper,
    prevArrowButton: carouselProps.prevArrowButton,
    nextArrowWrapper: carouselProps.nextArrowWrapper,
    nextArrowButton: carouselProps.nextArrowButton,
    carouselWrapper: carouselProps.carouselWrapper,
    carouselContainer: carouselProps.carouselContainer,
    carouselSlidesContainer: carouselProps.carouselSlidesContainer,
    carouselSlide: carouselProps.carouselSlide,
    carouselSlideInner: carouselProps.carouselSlideInner,
    dotsWrapper: carouselProps.dotsWrapper,
    dotButton: carouselProps.dotButton,
    dotButtonSelected: carouselProps.dotButtonSelected,
};

const Carousel = ({
    shellContext,
    theme,
    id,
    extendedStyles,
    title,
    maxNumberOfColumns,
    slides,
    // Starter Custom Code
    showDots,
    titleAlignment = textAlignmentOptions.center.id,
    useSkipNav = true,
    // Store Custom Code
    slideInterval = 0,
    startIndex = 0,
    slideChangeCallback,
    // Store Custom Code
    carouselClassNames,
}: Props) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const styles = useMergeProps(carouselStyles, extendedStyles);
    const [emblaRef, embla] = useEmblaCarousel();

    // Store Custom Code - >
    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);
    useRecursiveTimeout(scrollNext, 1000 * slideInterval);
    // Store Custom Code - <---

    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0); // Starter Custom Code
    // Store Custom Code
    const currentSlideIndex = embla?.selectedScrollSnap() ?? 0;
    // Store Custom Code - wrapped in usecallback to satisfy deps array
    const setCanScroll = useCallback(() => {
        if (shellContext.isInShell) {
            // Starter Removed - return
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    }, [embla, shellContext.isInShell]);

    /* Starter Custom Code - Start */
    const getSlidePerBreakpoint = (maxNumber: number) => {
        switch (maxNumber) {
            case 6:
                return [1, 2, 3, 4, 6];
            case 5:
                return [1, 2, 3, 4, 5];
            case 4:
                return [1, 2, 3, 4, 4];
            case 3:
                return [1, 2, 2, 3, 3];
            case 2:
                return [1, 2, 2, 2, 2];
            case 1:
                return [1, 1, 1, 1, 1];
            default:
                return [1, 2, 3, 4, maxNumber];
        }
    };
    /* Starter Custom Code - End */

    const getSlidesToScroll = useCallback(() => {
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        /* Starter Custom Code - Start */
        const slidePerBreakpoint = getSlidePerBreakpoint(maxNumberOfColumns);

        let localSlidesToScroll: number;
        const { innerWidth } = window;
        if (innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = slidePerBreakpoint[0];
        } else if (innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = slidePerBreakpoint[1];
        } else if (innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = slidePerBreakpoint[2];
        } else if (innerWidth < theme.breakpoints.values[4]) {
            localSlidesToScroll = slidePerBreakpoint[3];
        } else {
            localSlidesToScroll = slidePerBreakpoint[4];
        }
        /* Starter Custom Code - End */
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = useState(0);

    useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);
    }, [maxNumberOfColumns, windowResizeTime]);

    // Store Custom Code - debouncing the callback so we only get one call if people are scrolling quickly -->
    const debouncedCallBack = useMemo(
        () =>
            debounce(() => {
                slideChangeCallback?.(embla?.selectedScrollSnap() ?? 0);
            }, 300),
        [slideChangeCallback, embla],
    );
    // Store Custom Code - <---
    useEffect(() => {
        if (!embla) {
            return;
        }
        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);
        /* Starter Custom Code - Start */
        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
            debouncedCallBack();
        });
        // Store Custom Code - <---

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
        // Store Custom Code - fixed dependency array
    }, [debouncedCallBack, embla, setCanScroll]);

    useEffect(() => {
        if (!embla || !slides || slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            watchDrag: isTouchDevice(),
            loop: slides.length > slidesToScroll,
            /* Store Custom Code */
            startIndex,
        });
        setCanScroll();
    }, [embla, slidesToScroll, slides]);

    /* Store Custom Code - Start */
    useEffect(() => {
        if (!embla) {
            return;
        }
        embla.scrollTo(startIndex);
    }, [startIndex]);
    /* Store Custom Code - End */
    if (!slides || slides.length === 0) {
        return null;
    }

    const dotCount = slidesToScroll > 0 ? Math.ceil(slides.length / slidesToScroll) : 0;

    const getDots = () => {
        const dots = [];

        for (let i = 0; i < dotCount; i++) {
            dots.push(
                <Button
                    key={i} // eslint-disable-line react/no-array-index-key
                    /* Store Custom Code */
                    {...(i === currentSlideIndex ? styles.dotButtonSelected : styles.dotButton)}
                    aria-current={i === selectedIndex}
                    onClick={() => embla && embla.scrollTo(i)}
                    /* Store Custom Code */
                    className={`carousel-dot ${i === currentSlideIndex ? "selected" : ""} ${
                        carouselClassNames?.carouselDot || ""
                    }`}
                >
                    <VisuallyHidden>{translate("Go to slide {0} of {1}", `${i + 1}`, `${dotCount}`)}</VisuallyHidden>
                </Button>,
            );
        }

        return (
            <StyledWrapper
                {...styles.dotsWrapper}
                className={carouselClassNames?.dotsWrapper || "" /* Store Custom Code */}
            >
                {dots}
            </StyledWrapper>
        );
    };

    const titleCss = css`
        ${styles.titleText?.css}
        ${cssProp("text-align", titleAlignment)}
    `;
    /* Starter Custom Code - End */

    return (
        <>
            {/* Starter Moved - SkipNav - down */}
            {/* Starter Custom Code - Start */}
            {title ? (
                <Typography {...styles.titleText} className="carousel-title" css={titleCss} /* Store Custom Code */>
                    {title}
                </Typography>
            ) : null}
            {useSkipNav && (
                <SkipNav
                    text={translate("Skip Carousel")}
                    extendedStyles={styles.skipCarouselButton}
                    destination={afterCarousel}
                />
            )}
            {/* Starter Custom Code - End */}
            <StyledWrapper
                /* Store Custom Code */
                className={`carousel-wrapper ${carouselClassNames?.carouselWrapper || ""}`}
                {...styles.carouselWrapper}
                data-test-selector="carousel"
            >
                {/* Starter Custom Code - Start */}
                {slides.length > slidesToScroll && (
                    <StyledWrapper
                        /* Store Custom Code */
                        className={`carousel-button-wrapper arrow previous ${
                            carouselClassNames?.prevArrowWrapper || ""
                        }`}
                        {...styles.prevArrowWrapper}
                    >
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            disabled={!canScrollPrev}
                            data-test-selector="prevBtn"
                        >
                            <VisuallyHidden>{translate("Previous slide")}</VisuallyHidden>
                        </Button>
                    </StyledWrapper>
                )}
                {/* Starter Custom Code - End */}
                {/* Starter Removed - carouselGridItem */}
                {/* Starter Removed - carouselWrapper */}
                {/* Store Custom Code - Start */}
                <StyledWrapper
                    {...styles.carouselContainer}
                    className={`carousel-container ${carouselClassNames?.carouselContainer || ""}`}
                    ref={emblaRef}
                >
                    <StyledWrapper
                        {...styles.carouselSlidesContainer}
                        className={`carousel-slide-container ${carouselClassNames?.carouselSlidesContainer || ""}`}
                        data-test-selector="slides"
                    >
                        {slides.map(slide => (
                            <StyledWrapper
                                {...styles.carouselSlide}
                                key={slide.id}
                                style={{ width: `calc(100% / ${slidesToScroll})` }}
                                className={`carousel-slide ${carouselClassNames?.carouselSlide || ""}`}
                            >
                                <StyledWrapper
                                    className={`carousel-slide-inner ${carouselClassNames?.carouselSlideInner || ""}`}
                                    {...styles.carouselSlideInner}
                                    data-test-key={slide.id}
                                    id={`slide_${slide.id}`}
                                    data-test-selector="carousel_slide"
                                >
                                    {slide.renderComponent({})}
                                </StyledWrapper>
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
                {/* Store Custom Code - End */}
                {/* Starter Custom Code - Start */}
                {slides.length > slidesToScroll && (
                    <StyledWrapper
                        className={`carousel-button-wrapper arrow next ${carouselClassNames?.nextArrowWrapper || ""}`}
                        {...styles.nextArrowWrapper}
                    >
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            disabled={!canScrollNext}
                            data-test-selector="nextBtn"
                        >
                            <VisuallyHidden>{translate("Next slide")}</VisuallyHidden>
                        </Button>
                    </StyledWrapper>
                )}
                {/* Starter Custom Code - End */}
            </StyledWrapper>
            {showDots && dotCount > 1 ? getDots() : null /* Starter Custom Code */}
            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

Carousel.defaultProps = {
    numberOfColumns: 4,
};
export default withIsInShell(withTheme(Carousel));

// Starter carousel.tsx is exposing these functions and types so we need to add them here because the blueprint inheritance needs them in the final version of corousel.tsx

export const getItemsPerSlideFromFieldValue = (
    value: NumberTextFieldValue,
    parser: (...args: any) => BreakpointValue<number>[] | undefined,
) => {
    const number = Number(value);

    if (!Number.isNaN(number)) {
        return getItemsPerSlidePreset(number);
    }

    return parser(value);
};

export const getItemsPerSlidePreset = (maxNumber: number | undefined) => {
    if (!maxNumber) {
        return undefined;
    }

    switch (maxNumber) {
        case 1:
            return [1, 1, 1, 1, 1];
        case 2:
            return [1, 2, 2, 2, 2];
        case 3:
            return [1, 2, 2, 3, 3];
        case 4:
            return [1, 2, 3, 4, 4];
        case 5:
            return [1, 2, 3, 4, 5];
        case 6:
            return [1, 3, 4, 5, 6];
        default:
            return [1, 2, 3, 4, maxNumber];
    }
};

export type CarouselArrowsPositionVariantOptions = DataOptions<CarouselArrowsPositionVariantOption> &
    CarouselArrowsPositionVariantOptionMapper;
export type CarouselArrowsPositionVariant = keyof CarouselArrowsPositionVariantOptionMapper;

export const carouselArrowsPositionVariantOptions: CarouselArrowsPositionVariantOptions = {
    standard: {
        id: "standard",
        displayName: "Standard",
        isDefault: true,
        value: {
            positionOrigin: ["50%", "60%"],
        },
    },
    inside: { id: "inside", displayName: "Inside", isDefault: true, value: {} },
};

export interface CarouselArrowsPositionVariantOptionMapper {
    standard: CarouselArrowsPositionVariantOption;
    inside: CarouselArrowsPositionVariantOption;
}

export type CarouselAlign = "center" | "end" | "start";
export type CarouselAxis = "x" | "y";
export type CarouselItemsAlign = "flex-start" | "center" | "flex-end" | "stretch";
export type CarouselItemsJustify = "flex-start" | "center" | "flex-end" | DataType.ContentDistribution;

export type CarouselArrowsPositionVariantOption = DataOption<CarouselArrowPresentationProps>;

export const carouselDefaultCustomCss = `.carousel-wrapper {
}
.carousel-title {
}
.carousel-wrapper {
}
.carousel-button-wrapper.arrow.previous {
}
.carousel-button-wrapper.arrow.next {
}
.carousel-dot {
}
.carousel-dot.selected {
}
.carousel-container {
}
.carousel-slides-container {
}
.carousel-slide-inner {
}
.carousel-slide {
}`;
